import useWindowDimensions from "../../utils/width";
import moment from "moment-timezone";

const DayAnalyticsByDate = ({ trades, theme, runSpeficCal, showGross = false }) => {
  
  const { width } = useWindowDimensions();
  const isDark = theme[0] === "dark";

  // Calculate trades per day and % return
  const calculateDayAnalytics = (trades) => {
    if (!trades || trades.length === 0) {
      return {
        Monday: { trades: 0, return: 0, tradingDays: 0 },
        Tuesday: { trades: 0, return: 0, tradingDays: 0 },
        Wednesday: { trades: 0, return: 0, tradingDays: 0 },
        Thursday: { trades: 0, return: 0, tradingDays: 0 },
        Friday: { trades: 0, return: 0, tradingDays: 0 },
      };
    }

    const dayData = {
      Monday: { trades: 0, return: 0, tradingDays: 0 },
      Tuesday: { trades: 0, return: 0, tradingDays: 0 },
      Wednesday: { trades: 0, return: 0, tradingDays: 0 },
      Thursday: { trades: 0, return: 0, tradingDays: 0 },
      Friday: { trades: 0, return: 0, tradingDays: 0 },
    };

    const tradingDaysByWeek = {};

    trades.forEach(trade => {
      if (trade.entryDate) {
        const date = moment(trade.entryDate, "DD/MM/YY");
        const dayName = date.format("dddd");
        const weekKey = date.format("YYYY-ww"); // Year-week format

        // Initialize week if not exists
        if (!tradingDaysByWeek[weekKey]) {
          tradingDaysByWeek[weekKey] = new Set();
        }

        // Add this day to the week's trading days
        tradingDaysByWeek[weekKey].add(dayName);

        // Count trades for this day
        if (dayData[dayName]) {
          dayData[dayName].trades += 1;

          // Store this trade for later calculation with runSpeficCal
          if (!dayData[dayName].tradesList) {
            dayData[dayName].tradesList = [];
          }
          dayData[dayName].tradesList.push(trade);
        }
      }
    });

    // Calculate trading days for each day of the week
    Object.keys(tradingDaysByWeek).forEach(week => {
      const daysInWeek = tradingDaysByWeek[week];
      daysInWeek.forEach(day => {
        if (dayData[day]) {
          dayData[day].tradingDays += 1;
        }
      });
    });

    // Calculate return values and check for 0 to apply amber color
    Object.keys(dayData).forEach(dayName => {
      if (dayData[dayName].tradesList && dayData[dayName].tradesList.length > 0) {
        const dayTrades = dayData[dayName].tradesList;
        
        // Use runSpeficCal to get the return value
        const returnElement = runSpeficCal(dayTrades, false, showGross);
        
        // Extract the percentage value from the React element
        let returnValue = 0;
        if (returnElement && returnElement.props && returnElement.props.children) {
          const percentageText = returnElement.props.children;
          
          if (typeof percentageText === 'string') {
            // Extract numeric value from percentage string (e.g., "+2.35%" -> 2.35)
            const percentageMatch = percentageText.match(/([+-]?[\d.]+)/);
            if (percentageMatch) {
              returnValue = parseFloat(percentageMatch[1]);
            }
          } else if (typeof percentageText === 'number') {
            returnValue = percentageText;
          }
        }
        
        // Store the return value and trades list
        dayData[dayName].returnValue = returnValue;
        dayData[dayName].tradesList = dayTrades;
      }
    });

    return dayData;
  };

  const dayAnalytics = calculateDayAnalytics(trades);

  
  const renderDayCard = (dayName, data) => {
    const hasTrades = data.trades > 0;

    return (
      <div
        key={dayName}
        style={{
          backgroundColor: isDark ? "#25262b" : "#f9fafb",
          border: `1px solid ${isDark ? "#2d2e33" : "#e5e7eb"}`,
          borderRadius: "8px",
          padding: "16px",
          minWidth: width <= 768 ? "100px" : "120px",
          textAlign: "center",
          opacity: hasTrades ? 1 : 0.5,
        }}
      >
        <div
          style={{
            fontSize: width <= 768 ? "14px" : "16px",
            fontWeight: "600",
            color: isDark ? "#e5e7eb" : "#374151",
            marginBottom: "8px",
          }}
        >
          {dayName}
        </div>
        
        <div
          style={{
            fontSize: width <= 768 ? "20px" : "24px",
            fontWeight: "700",
            color: isDark ? "#e5e7eb" : "#111827",
            marginBottom: "4px",
          }}
        >
          {data.trades}
        </div>
        
        <div
          style={{
            fontSize: width <= 768 ? "11px" : "12px",
            color: isDark ? "#9ca3af" : "#6b7280",
            marginBottom: "8px",
          }}
        >
          trades
        </div>

        {hasTrades && (
          <>
            <div
              style={{
                fontSize: width <= 768 ? "14px" : "16px",
                fontWeight: "600",
                marginBottom: "4px",
              }}
            >
              {data.tradesList && data.tradesList.length > 0 && (
              (() => {
                const tdElement = runSpeficCal(data.tradesList, false, showGross);
                if (tdElement && tdElement.props) {
                  const { children, style } = tdElement.props;
                  return (
                    <span style={style}>
                      {children}
                    </span>
                  );
                }
                return tdElement;
              })()
            )}
            </div>
            
            <div
              style={{
                fontSize: width <= 768 ? "10px" : "11px",
                color: isDark ? "#9ca3af" : "#6b7280",
              }}
            >
              {data.tradingDays} trading {data.tradingDays === 1 ? "day" : "days"}
            </div>
          </>
        )}

        {!hasTrades && (
          <div
            style={{
              fontSize: width <= 768 ? "12px" : "14px",
              color: isDark ? "#6b7280" : "#9ca3af",
              fontStyle: "italic",
            }}
          >
            No trading
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      style={{
        padding: "20px",
        borderRadius: "12px",
        width: "100%",
        maxWidth: "770px",
        backgroundColor: isDark ? "#1e1f23" : "#ffffff",
        border: `1px solid ${isDark ? "#2d2e33" : "#e5e7eb"}`,
        marginTop: "16px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          fontSize: width <= 700 ? "16px" : "18px",
          fontWeight: "600",
          color: isDark ? "#e5e7eb" : "#374151",
          marginBottom: "16px",
        }}
      >
        Trading Days
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-around",
          alignItems: "center",
          flexWrap: width <= 768 ? "wrap" : "nowrap",
          gap: width <= 768 ? "12px" : "8px",
          marginBottom: "16px",
        }}
      >
        {renderDayCard("Monday", dayAnalytics.Monday)}
        {renderDayCard("Tuesday", dayAnalytics.Tuesday)}
        {renderDayCard("Wednesday", dayAnalytics.Wednesday)}
        {renderDayCard("Thursday", dayAnalytics.Thursday)}
        {renderDayCard("Friday", dayAnalytics.Friday)}
      </div>

      
    </div>
  );
};

export default DayAnalyticsByDate;
